import api from "@utils/api.client"
import { useEffect, useMemo, useState } from "react"
import styled from "styled-components"

const Section = styled.section`
  position: relative;
  width: 100%;
  display: flex;
  justify-content: center;
  padding: clamp(4.5rem, 10vw, 6.5rem) 0;
  background: linear-gradient(180deg, #f5f8fb 0%, #dfe9f8 100%);
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: -40% 30% auto 30%;
    height: 60%;
    background: radial-gradient(circle, rgba(99, 195, 255, 0.25), transparent 70%);
    filter: blur(60px);
    opacity: 0.9;
  }
`

const Content = styled.div`
  position: relative;
  z-index: 1;
  width: min(1150px, 92vw);
  display: grid;
  gap: clamp(2.2rem, 6vw, 3.4rem);
`

const SectionHeader = styled.header`
  display: grid;
  gap: 0.75rem;
  text-align: center;
  justify-items: center;
`

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.45rem 1.25rem;
  border-radius: 999px;
  background: rgba(27, 117, 188, 0.12);
  color: #1b75bc;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.18em;
  text-transform: uppercase;
`

const Title = styled.h2`
  margin: 0;
  font-size: clamp(2.1rem, 4.5vw, 2.8rem);
  color: #0a2743;
`

const Subtitle = styled.p`
  margin: 0;
  max-width: 640px;
  font-size: 1.05rem;
  line-height: 1.75;
  color: rgba(10, 39, 67, 0.75);
`

const ReviewsGrid = styled.div`
  display: grid;
  gap: clamp(1.5rem, 4vw, 2.4rem);
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
`

const ReviewCard = styled.article`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: clamp(1.6rem, 4vw, 2.15rem);
  border-radius: 22px;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.85)),
    radial-gradient(circle at 20% -10%, rgba(99, 195, 255, 0.25), transparent 70%);
  border: 1px solid rgba(27, 117, 188, 0.14);
  box-shadow: 0 30px 55px rgba(15, 63, 115, 0.12);
  backdrop-filter: blur(10px);
`

const AuthorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.85rem;
`

const Avatar = styled.img`
  width: 52px;
  height: 52px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid rgba(27, 117, 188, 0.35);
  box-shadow: 0 8px 20px rgba(15, 63, 115, 0.15);
`

const FallbackAvatar = styled.span`
  width: 52px;
  height: 52px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  color: #0a2743;
  background: linear-gradient(135deg, rgba(99, 195, 255, 0.45), rgba(99, 255, 202, 0.6));
  box-shadow: 0 8px 20px rgba(15, 63, 115, 0.15);
`

const AuthorName = styled.span`
  font-weight: 700;
  font-size: 1.1rem;
  color: #0a2743;
`

const Rating = styled.div`
  display: inline-flex;
  gap: 0.2rem;
  color: #f4b400;
  font-size: 1.1rem;
`

const ReviewText = styled.p`
  margin: 0;
  font-size: 1rem;
  line-height: 1.7;
  color: rgba(10, 39, 67, 0.85);
`

const ReviewMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
  padding-top: 0.6rem;
  border-top: 1px solid rgba(27, 117, 188, 0.12);
  font-size: 0.85rem;
  color: rgba(10, 39, 67, 0.55);
`

const ReviewLink = styled.a`
  color: #1b75bc;
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`

const FooterLinkRow = styled(ReviewMeta)`
  margin-top: 0;
  padding-top: 0;
  border-top: none;
  justify-content: center;
  gap: 0.4rem;
`

interface Review {
  profilePhoto?: string
  author: string
  rating: number
  text: string
  time?: string
  googleMapsUri?: string
  lastUpdated?: string
  createdAt?: string
}

const formatRelativeTime = (timestamp?: string) => {
  if (!timestamp) return ""
  const date = new Date(timestamp)
  if (Number.isNaN(date.getTime())) return ""
  const diffMs = Date.now() - date.getTime()
  if (diffMs < 0) return ""
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  if (diffDays < 1) return "Aujourd'hui"
  if (diffDays < 7) return `Il y a ${diffDays} jour${diffDays > 1 ? "s" : ""}`
  const diffWeeks = Math.floor(diffDays / 7)
  if (diffWeeks < 5) return `Il y a ${diffWeeks} semaine${diffWeeks > 1 ? "s" : ""}`
  const diffMonths = Math.floor(diffDays / 30)
  if (diffMonths < 12) return `Il y a ${diffMonths} mois`
  const diffYears = Math.floor(diffDays / 365)
  return `Il y a ${diffYears} an${diffYears > 1 ? "s" : ""}`
}

const GoogleReviews = () => {
  const [reviews, setReviews] = useState<Review[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await api.get<Review[]>("/google-reviews")
        setReviews(response.data)
      } catch {
        setReviews([])
      } finally {
        setIsLoading(false)
      }
    }

    fetchReviews()
  }, [])

  const sortedReviews = useMemo(() => {
    if (reviews.length === 0) return reviews
    const hasTimestamp = reviews.some((review) => Boolean(review.lastUpdated || review.createdAt))
    if (!hasTimestamp) return reviews
    return [...reviews].sort((a, b) => {
      const aTime = new Date(a.lastUpdated ?? a.createdAt ?? 0).getTime()
      const bTime = new Date(b.lastUpdated ?? b.createdAt ?? 0).getTime()
      if (!aTime && !bTime) return 0
      if (!aTime) return 1
      if (!bTime) return -1
      return bTime - aTime
    })
  }, [reviews])

  const googleMapsLink = useMemo(() => {
    if (sortedReviews.length === 0) return ""
    const withLink = sortedReviews.find((review) => Boolean(review.googleMapsUri))
    return withLink?.googleMapsUri ?? sortedReviews[0]?.googleMapsUri ?? ""
  }, [sortedReviews])

  if (isLoading || reviews.length === 0) return null

  return (
    <Section>
      <Content>
        <SectionHeader>
          <Badge>Ils nous recommandent</Badge>
          <Title>Avis sur Google</Title>
          <Subtitle>Des commentaires publiés par des propriétaires et des locataires sur leur expérience avec notre équipe.</Subtitle>
        </SectionHeader>

        {
          <>
            <ReviewsGrid>
              {sortedReviews.map((review) => {
                const initials = review.author
                  .split(" ")
                  .filter(Boolean)
                  .map((part) => part[0])
                  .slice(0, 2)
                  .join("")

                const displayTime = review.time?.trim() || formatRelativeTime(review.lastUpdated ?? review.createdAt)
                const hasMeta = Boolean(displayTime || review.googleMapsUri)

                return (
                  <ReviewCard key={`${review.author}-${review.time}`}>
                    <AuthorRow>
                      {review.profilePhoto ? (
                        <Avatar src={review.profilePhoto} alt={review.author} loading="lazy" />
                      ) : (
                        <FallbackAvatar aria-hidden="true">{initials}</FallbackAvatar>
                      )}
                      <AuthorName>{review.author}</AuthorName>
                    </AuthorRow>
                    <Rating aria-label={`${review.rating} étoiles`}>
                      {Array.from({ length: review.rating }).map((_, index) => (
                        <span key={index}>★</span>
                      ))}
                    </Rating>
                    <ReviewText>“{review.text}”</ReviewText>
                    {hasMeta && (
                      <ReviewMeta>
                        {displayTime && <span>{displayTime}</span>}
                        {review.googleMapsUri && (
                          <ReviewLink href={review.googleMapsUri} target="_blank" rel="noopener noreferrer">
                            Voir l'avis
                          </ReviewLink>
                        )}
                      </ReviewMeta>
                    )}
                  </ReviewCard>
                )
              })}
            </ReviewsGrid>

            {googleMapsLink && (
              <FooterLinkRow>
                <ReviewLink href={googleMapsLink} target="_blank" rel="noopener noreferrer">
                  Découvrir tous les avis Google →
                </ReviewLink>
              </FooterLinkRow>
            )}
          </>
        }
      </Content>
    </Section>
  )
}

export default GoogleReviews
